import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'featured' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group block rounded-xl overflow-hidden relative cursor-pointer min-h-[400px]"
      >
        <div className="relative h-full min-h-[400px]">
          <img
            src={article.coverImage}
            alt={article.title}
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-5">
            <span className="inline-block px-2 py-0.5 bg-accent-500 text-background-50 text-xs rounded-full mb-3">
              {article.category}
            </span>
            <h3 className="text-background-50 font-bold text-lg md:text-xl leading-snug mb-2 line-clamp-3">
              {article.title}
            </h3>
            <p className="text-background-200 text-sm line-clamp-2 mb-3">{article.excerpt}</p>
            <div className="flex items-center gap-3 text-background-300 text-xs">
              <span><i className="ri-eye-line mr-1"></i>{(article.viewCount / 10000).toFixed(1)}万阅读</span>
              <span>{article.publishDate}</span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex gap-4 bg-background-50 rounded-xl overflow-hidden transition-all duration-300 hover:scale-[1.01] cursor-pointer p-3"
      >
        <div className="w-24 h-24 md:w-32 md:h-32 flex-shrink-0 rounded-lg overflow-hidden">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
          />
        </div>
        <div className="flex flex-col justify-between flex-1 min-w-0 py-1">
          <div>
            <span className="inline-block px-1.5 py-0.5 bg-accent-100 text-accent-700 text-xs rounded-full mb-1">
              {article.category}
            </span>
            <h3 className="text-foreground-900 font-medium text-sm leading-snug line-clamp-2">
              {article.title}
            </h3>
          </div>
          <div className="flex items-center gap-3 text-foreground-500 text-xs">
            <span><i className="ri-eye-line mr-1"></i>{(article.viewCount / 10000).toFixed(1)}万</span>
            <span>{article.publishDate}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className="group block bg-background-50 rounded-xl overflow-hidden transition-all duration-300 hover:scale-[1.02] cursor-pointer"
    >
      <div className="aspect-[16/10] overflow-hidden relative">
        <img
          src={article.coverImage}
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        <span className="absolute top-3 left-3 px-2 py-0.5 bg-accent-500/90 text-background-50 text-xs rounded-full">
          {article.category}
        </span>
      </div>
      <div className="p-4">
        <h3 className="text-foreground-900 font-medium text-sm md:text-base leading-snug line-clamp-2 mb-2">
          {article.title}
        </h3>
        <p className="text-foreground-500 text-xs line-clamp-2 mb-3">{article.excerpt}</p>
        <div className="flex items-center justify-between text-foreground-500 text-xs">
          <span><i className="ri-eye-line mr-1"></i>{(article.viewCount / 10000).toFixed(1)}万</span>
          <span>{article.publishDate}</span>
        </div>
      </div>
    </Link>
  );
}